/**
 * Code generators for TypeScript, Go, Zod, and more
 * Generates type definitions from parsed data
 */

export type CodeGenLanguage = 'typescript' | 'go' | 'zod' | 'python' | 'rust'

interface TypeInfo {
  name: string
  isArray: boolean
  isOptional: boolean
  children?: Record<string, TypeInfo>
  arrayItemType?: TypeInfo
}

/**
 * Infer type information from a value
 */
function inferType(value: unknown, path: string = 'Root'): TypeInfo {
  if (value === null || value === undefined) {
    return { name: 'null', isArray: false, isOptional: true }
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return {
        name: 'array',
        isArray: true,
        isOptional: false,
        arrayItemType: { name: 'unknown', isArray: false, isOptional: false }
      }
    }

    // Infer item type from first element (could be improved with union types)
    const itemType = inferType(value[0], `${path}Item`)
    return {
      name: 'array',
      isArray: true,
      isOptional: false,
      arrayItemType: itemType
    }
  }

  if (typeof value === 'object') {
    const children: Record<string, TypeInfo> = {}
    for (const [key, val] of Object.entries(value)) {
      const pascalKey = key.charAt(0).toUpperCase() + key.slice(1)
      children[key] = inferType(val, `${path}${pascalKey}`)
    }
    return {
      name: 'object',
      isArray: false,
      isOptional: false,
      children
    }
  }

  if (typeof value === 'string') {
    return { name: 'string', isArray: false, isOptional: false }
  }

  if (typeof value === 'number') {
    return {
      name: Number.isInteger(value) ? 'integer' : 'number',
      isArray: false,
      isOptional: false
    }
  }

  if (typeof value === 'boolean') {
    return { name: 'boolean', isArray: false, isOptional: false }
  }

  return { name: 'unknown', isArray: false, isOptional: false }
}

function toPascalCase(name: string): string {
  return name
    .split(/[_\-\s]+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('')
}

function toCamelCase(name: string): string {
  const pascal = toPascalCase(name)
  return pascal.charAt(0).toLowerCase() + pascal.slice(1)
}

function toSnakeCase(name: string): string {
  return name
    .replace(/([A-Z])/g, '_$1')
    .toLowerCase()
    .replace(/^_/, '')
    .replace(/[^a-z0-9_]/g, '_')
}

// ============== TypeScript Generator ==============

function typeInfoToTS(info: TypeInfo, interfaces: Map<string, string>, name: string): string {
  if (info.name === 'null') return 'null'
  if (info.name === 'string') return 'string'
  if (info.name === 'number' || info.name === 'integer') return 'number'
  if (info.name === 'boolean') return 'boolean'
  if (info.name === 'unknown') return 'unknown'

  if (info.isArray && info.arrayItemType) {
    const itemType = typeInfoToTS(info.arrayItemType, interfaces, `${name}Item`)
    return `${itemType}[]`
  }

  if (info.name === 'object' && info.children) {
    const interfaceName = toPascalCase(name)
    const fields = Object.entries(info.children)
      .map(([key, childInfo]) => {
        const safeKey = /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(key) ? key : `"${key}"`
        const childType = typeInfoToTS(childInfo, interfaces, `${name}${toPascalCase(key)}`)
        const optional = childInfo.isOptional ? '?' : ''
        return `  ${safeKey}${optional}: ${childType};`
      })
      .join('\n')

    interfaces.set(interfaceName, `export interface ${interfaceName} {\n${fields}\n}`)
    return interfaceName
  }

  return 'unknown'
}

export function generateTypeScript(data: unknown, rootName: string = 'Root'): string {
  const interfaces = new Map<string, string>()
  const typeInfo = inferType(data, rootName)

  typeInfoToTS(typeInfo, interfaces, rootName)

  const result = Array.from(interfaces.values()).join('\n\n')

  return `// Auto-generated TypeScript interfaces
// Generated by AIQIA DataVision

${result}
`
}

// ============== Zod Generator ==============

function typeInfoToZod(info: TypeInfo, schemas: Map<string, string>, name: string): string {
  if (info.name === 'null') return 'z.null()'
  if (info.name === 'string') return 'z.string()'
  if (info.name === 'number') return 'z.number()'
  if (info.name === 'integer') return 'z.number().int()'
  if (info.name === 'boolean') return 'z.boolean()'
  if (info.name === 'unknown') return 'z.unknown()'

  if (info.isArray && info.arrayItemType) {
    const itemSchema = typeInfoToZod(info.arrayItemType, schemas, `${name}Item`)
    return `z.array(${itemSchema})`
  }

  if (info.name === 'object' && info.children) {
    const schemaName = toCamelCase(name) + 'Schema'
    const fields = Object.entries(info.children)
      .map(([key, childInfo]) => {
        const safeKey = /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(key) ? key : `"${key}"`
        let childSchema = typeInfoToZod(childInfo, schemas, `${name}${toPascalCase(key)}`)
        if (childInfo.isOptional) {
          childSchema += '.optional()'
        }
        return `  ${safeKey}: ${childSchema},`
      })
      .join('\n')

    schemas.set(schemaName, `export const ${schemaName} = z.object({\n${fields}\n});

export type ${toPascalCase(name)} = z.infer<typeof ${schemaName}>;`)
    return schemaName
  }

  return 'z.unknown()'
}

export function generateZod(data: unknown, rootName: string = 'Root'): string {
  const schemas = new Map<string, string>()
  const typeInfo = inferType(data, rootName)

  typeInfoToZod(typeInfo, schemas, rootName)

  const result = Array.from(schemas.values()).join('\n\n')

  return `// Auto-generated Zod schemas
// Generated by AIQIA DataVision

import { z } from 'zod';

${result}
`
}

// ============== Go Generator ==============

function typeInfoToGo(info: TypeInfo, structs: Map<string, string>, name: string): string {
  if (info.name === 'null') return 'interface{}'
  if (info.name === 'string') return 'string'
  if (info.name === 'number') return 'float64'
  if (info.name === 'integer') return 'int64'
  if (info.name === 'boolean') return 'bool'
  if (info.name === 'unknown') return 'interface{}'

  if (info.isArray && info.arrayItemType) {
    const itemType = typeInfoToGo(info.arrayItemType, structs, `${name}Item`)
    return `[]${itemType}`
  }

  if (info.name === 'object' && info.children) {
    const structName = toPascalCase(name)
    const fields = Object.entries(info.children)
      .map(([key, childInfo]) => {
        const fieldName = toPascalCase(key)
        const goType = typeInfoToGo(childInfo, structs, `${name}${fieldName}`)
        const jsonTag = `\`json:"${key}${childInfo.isOptional ? ',omitempty' : ''}"\``
        const pointer = childInfo.isOptional ? '*' : ''
        return `\t${fieldName} ${pointer}${goType} ${jsonTag}`
      })
      .join('\n')

    structs.set(structName, `type ${structName} struct {\n${fields}\n}`)
    return structName
  }

  return 'interface{}'
}

export function generateGo(data: unknown, rootName: string = 'Root', packageName: string = 'main'): string {
  const structs = new Map<string, string>()
  const typeInfo = inferType(data, rootName)

  typeInfoToGo(typeInfo, structs, rootName)

  const result = Array.from(structs.values()).join('\n\n')

  return `// Auto-generated Go structs
// Generated by AIQIA DataVision

package ${packageName}

${result}
`
}

// ============== Python Generator ==============

function typeInfoToPython(info: TypeInfo, classes: Map<string, string>, name: string): string {
  if (info.name === 'null') return 'None'
  if (info.name === 'string') return 'str'
  if (info.name === 'number' || info.name === 'integer') return 'int' // Python uses int for both
  if (info.name === 'boolean') return 'bool'
  if (info.name === 'unknown') return 'Any'

  if (info.isArray && info.arrayItemType) {
    const itemType = typeInfoToPython(info.arrayItemType, classes, `${name}Item`)
    return `List[${itemType}]`
  }

  if (info.name === 'object' && info.children) {
    const className = toPascalCase(name)
    const fields = Object.entries(info.children)
      .map(([key, childInfo]) => {
        const fieldName = toSnakeCase(key)
        let pyType = typeInfoToPython(childInfo, classes, `${name}${toPascalCase(key)}`)
        if (childInfo.isOptional) {
          pyType = `Optional[${pyType}]`
        }
        return `    ${fieldName}: ${pyType}`
      })
      .join('\n')

    classes.set(className, `@dataclass\nclass ${className}:\n${fields}`)
    return className
  }

  return 'Any'
}

export function generatePython(data: unknown, rootName: string = 'Root'): string {
  const classes = new Map<string, string>()
  const typeInfo = inferType(data, rootName)

  typeInfoToPython(typeInfo, classes, rootName)

  const result = Array.from(classes.values()).join('\n\n\n')

  return `# Auto-generated Python dataclasses
# Generated by AIQIA DataVision

from dataclasses import dataclass
from typing import List, Optional, Any


${result}
`
}

// ============== Rust Generator ==============

function typeInfoToRust(info: TypeInfo, structs: Map<string, string>, name: string): string {
  if (info.name === 'null') return '()'
  if (info.name === 'string') return 'String'
  if (info.name === 'number') return 'f64'
  if (info.name === 'integer') return 'i64'
  if (info.name === 'boolean') return 'bool'
  if (info.name === 'unknown') return 'serde_json::Value'

  if (info.isArray && info.arrayItemType) {
    const itemType = typeInfoToRust(info.arrayItemType, structs, `${name}Item`)
    return `Vec<${itemType}>`
  }

  if (info.name === 'object' && info.children) {
    const structName = toPascalCase(name)
    const fields = Object.entries(info.children)
      .map(([key, childInfo]) => {
        const fieldName = toSnakeCase(key)
        let rustType = typeInfoToRust(childInfo, structs, `${name}${toPascalCase(key)}`)
        if (childInfo.isOptional) {
          rustType = `Option<${rustType}>`
        }
        const serdeAttr = fieldName !== key ? `    #[serde(rename = "${key}")]\n` : ''
        return `${serdeAttr}    pub ${fieldName}: ${rustType},`
      })
      .join('\n')

    structs.set(structName, `#[derive(Debug, Clone, Serialize, Deserialize)]\npub struct ${structName} {\n${fields}\n}`)
    return structName
  }

  return 'serde_json::Value'
}

export function generateRust(data: unknown, rootName: string = 'Root'): string {
  const structs = new Map<string, string>()
  const typeInfo = inferType(data, rootName)

  typeInfoToRust(typeInfo, structs, rootName)

  const result = Array.from(structs.values()).join('\n\n')

  return `// Auto-generated Rust structs
// Generated by AIQIA DataVision

use serde::{Deserialize, Serialize};

${result}
`
}

// ============== Main Export ==============

export function generateCode(
  data: unknown,
  language: CodeGenLanguage,
  rootName: string = 'Root'
): string {
  switch (language) {
    case 'typescript':
      return generateTypeScript(data, rootName)
    case 'zod':
      return generateZod(data, rootName)
    case 'go':
      return generateGo(data, rootName)
    case 'python':
      return generatePython(data, rootName)
    case 'rust':
      return generateRust(data, rootName)
    default:
      return generateTypeScript(data, rootName)
  }
}

export function getLanguageFileExtension(language: CodeGenLanguage): string {
  switch (language) {
    case 'typescript': return 'ts'
    case 'zod': return 'ts'
    case 'go': return 'go'
    case 'python': return 'py'
    case 'rust': return 'rs'
    default: return 'txt'
  }
}

export function getLanguageLabel(language: CodeGenLanguage): string {
  switch (language) {
    case 'typescript': return 'TypeScript'
    case 'zod': return 'Zod Schema'
    case 'go': return 'Go'
    case 'python': return 'Python'
    case 'rust': return 'Rust'
    default: return language
  }
}
